// DONE 
// - kill swipe to open (maybe move it to footer only?)
// fix footer height
// - order drafts by recent timestamps
// fix footer height
// move new post to bottom of draft
// unfocus input when draft open
// focus input when new post button pressed
// - add oauth
// - add syncing
// - simple import/export
// - text area gets wonky with long posts ... why did this take so long?
// - make it back up to cloud
// - fix drafts (desktop)
// - vertically center @accountsheet
// - add intent posting
// - add first post as draft for how litewrite works
//
//
// TODO for RELEASE
// - add OG scraper
// 

// other TODOs
// - auto-threads
// - chrome extension test
//  - make it SIWE
// - add UI themes
// - add nitter scraper
// - add thread preview
// - draft delete button precarious w/ no confirmation, reconsider
// - add attachments


import React, { useMemo, useState, useEffect } from 'react';
import Editor from './components/Editor';
import RouteEditor from './components/RouteEditor';
import EditorDemo from './components/EditorDemo';
import DraftRouteGuard from './components/DraftRouteGuard';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import Footer from './components/Footer';
import { DraftProvider } from './contexts/DraftContext';
import { useDrafts } from './contexts/DraftContext';
import { isNewUser } from './config/onboarding';
import './App.css';
import { PlatformProvider } from './contexts/PlatformContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { useHotkeys, HOTKEYS } from './utils/hotkeys';
import { useAuth } from './lib/useAuth';
import { AccountPage } from './components/AccountPage';
import { CrosspostModal } from './components/CrosspostModal';
import { MAX_CHARS_FOR_UI, canAttemptPost } from './utils/posting';
import { useAttachments } from './hooks/useAttachments';
import { AttachmentOverlay } from './components/AttachmentOverlay';
import { AttachmentErrorBoundary } from './components/AttachmentErrorBoundary';
import { PostPreviewModal } from './components/PostPreviewModal';
import { useSettings } from './contexts/SettingsContext';
import { useKeyboardState } from './hooks/useKeyboardState';
import { parseClarityExamples, requestClarityPreview, ClarityVariant } from './utils/clarity';
import { TopBarActions } from './components/TopBarActions';
import { usePlatforms } from './contexts/PlatformContext';

interface DraftsManagerProps {
  EditorComponent?: React.ComponentType<{ isEditable?: boolean }>;
}

function syncViewportHeightVar() {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const value = `${viewportHeight}px`;
  if (document.documentElement.style.getPropertyValue('--app-height') !== value) {
    document.documentElement.style.setProperty('--app-height', value);
  }
}

// Separate component so we can use context hooks
function DraftsManager({ EditorComponent = Editor }: DraftsManagerProps) {
  const navigate = useNavigate();
  const { currentDraft, saveDraft, savedDrafts, linkedPost, linkedPostMode, clearLinkedPost, deleteDraft, createDraftWithText } = useDrafts();
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  // const [shouldFocusEditor, setShouldFocusEditor] = useState(false);
  const isKeyboardOpen = useKeyboardState();
  const { session } = useAuth();
  const { settings } = useSettings();
  const { enabledPlatforms, platformConnections, postingMethods } = usePlatforms();
  const [crosspostPreviewMode] = useState<'default' | 'failed' | null>(() => {
    const value = new URLSearchParams(window.location.search).get('crosspost');
    if (value === 'true') return 'default';
    if (value === 'failed') return 'failed';
    return null;
  });
  const [attachmentsTestMode] = useState(() => (
    process.env.NODE_ENV !== 'production'
      && new URLSearchParams(window.location.search).get('attachmentsTest') === 'true'
  ));
  const [isCrosspostModalOpen, setIsCrosspostModalOpen] = useState(Boolean(crosspostPreviewMode));
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewVariants, setPreviewVariants] = useState<Record<string, ClarityVariant>>({});
  const [previewGenres, setPreviewGenres] = useState<string[]>([]);
  const [selectedGenre, setSelectedGenre] = useState('default');
  const [previewSelection, setPreviewSelection] = useState<'original' | 'edited'>('edited');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [postContent, setPostContent] = useState<string>('');
  const { items: attachments, isUploading: isUploadingAttachments, add: addAttachments, remove: removeAttachment } = useAttachments(
    currentDraft?.id,
    session?.user?.id,
    attachmentsTestMode
  );

  // Reply/Quote context - prefer DraftContext's linkedPost, fall back to URL params
  const replyToPostId = linkedPostMode === 'reply' ? linkedPost?.id : undefined;
  const quotePostId = linkedPostMode === 'quote' ? linkedPost?.id : undefined;

  const { id } = useParams<{ id: string }>();
  const showFooter = !!id;
  const showAttachmentOverlay = Boolean(id && currentDraft && currentDraft.id === id);

  // Check if user is new
  useEffect(() => {
    isNewUser(savedDrafts);
  }, [savedDrafts]);

  // Use plain text length for character counting (visual only)
  const plainText = currentDraft?.plainText || '';
  const plainTextLength = plainText.length;
  const charPercentage = Math.min((plainTextLength / MAX_CHARS_FOR_UI) * 100, 100);
  const isOverLimit = plainTextLength > MAX_CHARS_FOR_UI;
  const hasContent = canAttemptPost(plainText, posting);
  const hasOauthPostTarget = useMemo(() => {
    return Object.keys(enabledPlatforms).some((platform) => {
      const key = platform as keyof typeof enabledPlatforms;
      return enabledPlatforms[key] && platformConnections[key] && postingMethods[key] === 'oauth';
    });
  }, [enabledPlatforms, platformConnections, postingMethods]);
  const canAddAttachments = hasOauthPostTarget || attachmentsTestMode;
  const handleStartPosting = async (contentToPost: string) => {
    if (!currentDraft) return;
    console.log('handlePost called - setting modal to true');
    setPostContent(contentToPost);
    setPosting(true);
    setIsCrosspostModalOpen(true);
    console.log('handlePost - after setting modal to true');
  };

  const fallbackVariant = useMemo(() => ({ editedText: plainText, reasoning: '' }), [plainText]);

  const handleOpenPreview = async () => {
    if (!currentDraft) return;
    setIsPreviewModalOpen(true);
    setPreviewSelection('original');
    setPreviewError(null);
    const parsedExamples = parseClarityExamples(settings?.clarityExamples);
    const baseVariants: Record<string, ClarityVariant> = parsedExamples
      ? parsedExamples.genres.reduce((acc, genre) => {
          acc[genre] = { ...fallbackVariant };
          return acc;
        }, {} as Record<string, ClarityVariant>)
      : { default: { ...fallbackVariant } };
    setPreviewVariants(baseVariants);
    if (parsedExamples) {
      setPreviewGenres(parsedExamples.genres);
      setSelectedGenre(parsedExamples.genres[0]);
    } else {
      setPreviewGenres([]);
      setSelectedGenre('default');
    }

    if (!plainText.trim()) {
      setPreviewError('Add some text to preview your post.');
      return;
    }

    setPreviewSelection('edited');
    setPreviewLoading(true);
    try {
      if (parsedExamples) {
        const results = await Promise.allSettled(
          parsedExamples.genres.map(async (genre) => {
            const examples = parsedExamples.examplesByGenre[genre].join('\n\n');
            const variant = await requestClarityPreview(plainText, examples, genre);
            return { genre, variant };
          })
        );
        const nextVariants = { ...baseVariants };
        results.forEach((result, index) => {
          const genre = parsedExamples.genres[index];
          if (result.status === 'fulfilled') {
            nextVariants[result.value.genre] = result.value.variant;
          } else {
            // Store error in the specific variant
            nextVariants[genre] = {
              ...nextVariants[genre],
              error: 'Could not generate this version'
            };
          }
        });
        setPreviewVariants(nextVariants);
      } else {
        const variant = await requestClarityPreview(plainText, settings?.clarityExamples || '');
        setPreviewVariants({ default: variant });
      }
    } catch (error) {
      console.error('Clarity preview failed:', error);
      // Store error in the default variant
      setPreviewVariants({ default: { ...fallbackVariant, error: 'Could not generate clarity edit' } });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmPreview = () => {
    const activeVariant = previewVariants[selectedGenre] || previewVariants.default || fallbackVariant;
    const selectedContent = previewSelection === 'edited' ? activeVariant.editedText : plainText;
    setIsPreviewModalOpen(false);
    handleStartPosting(selectedContent);
  };

  // Add hotkey handler for cmd-enter
  useHotkeys([
    {
      ...HOTKEYS.POST,
      handler: () => {
        if (!posting && content.trim().length > 0) {
          handleOpenPreview();
        }
      }
    }
  ]);

  // Load current draft on mount or when it changes
  useEffect(() => {
    if (currentDraft && currentDraft.raw !== content) {
      setContent(currentDraft.raw);
    }
  }, [currentDraft, content]);


  // Auto-save draft when content changes
  useEffect(() => {
    // Skip empty content to prevent unnecessary saves
    if (!content.trim()) return;

    // Use a longer debounce to prevent rapid saves
    const timeoutId = setTimeout(() => {
      console.log('Auto-saving draft...');
      saveDraft(content, {
        twitter: true,
        bluesky: true,
        farcaster: true
      });
    }, 2000); // Increased from 1000ms to 2000ms

    return () => {
      console.log('Clearing auto-save timeout');
      clearTimeout(timeoutId);
    };
  }, [content, saveDraft]);

  const handleCrosspostModalClose = async (result?: { allSuccessful: boolean }) => {
    setIsCrosspostModalOpen(false);
    setPosting(false);
    setPostContent('');
    
    if (result?.allSuccessful && currentDraft?.id) {
      // Only clear linked post after successful posting
      if (linkedPost) {
        clearLinkedPost();
      }
      
      // Navigate to home (defaults to recent/drafts tab)
      navigate('/');
      try {
        await deleteDraft(currentDraft.id);
      } catch (error) {
        console.error('Failed to delete draft after posting:', error);
      }
    }
    // If posting failed, keep the linked post attached to the draft
  };

  const activeVariant = previewVariants[selectedGenre] || previewVariants.default || fallbackVariant;

  return (
    <div className={`App${showFooter ? '' : ' no-footer'}`} data-keyboard={isKeyboardOpen ? "1" : "0"}>
      <div className="main-scroll-container">
        <main className="editor-container">
          <TopBarActions
            variant="muted"
            navLabel={showFooter ? 'Draft navigation' : 'Account navigation'}
            actions={[
              showFooter
                ? {
                    icon: 'close',
                    label: 'Close draft',
                    onClick: () => navigate('/'),
                  }
                : {
                    icon: 'account',
                    label: 'Account',
                    onClick: () => navigate('/account'),
                  },
            ]}
          />
          <EditorComponent
            isEditable={!posting}
          />
          {showAttachmentOverlay && (
            <AttachmentErrorBoundary
              onError={(error, errorInfo) => {
                console.error('Attachment error:', error, errorInfo);
              }}
            >
              <AttachmentOverlay attachments={attachments} isUploading={isUploadingAttachments} onRemove={removeAttachment} />
            </AttachmentErrorBoundary>
          )}
        </main>
      </div>
      {showFooter && (
        <AttachmentErrorBoundary
          onError={(error, errorInfo) => {
            console.error('Footer attachment error:', error, errorInfo);
          }}
        >
          <Footer 
            charPercentage={charPercentage}
            isOverLimit={isOverLimit}
            onPost={
              () => {
                console.log('Footer Post button clicked - calling onPost', hasContent, posting);
                handleOpenPreview();
              }
            }

            hasContent={hasContent}
            posting={posting}
            content={plainText}
            primaryActionLabel="Preview"
            onAddAttachments={addAttachments}
            canAddAttachments={canAddAttachments}
            addAttachmentsDisabledReason={
              !session
                ? 'Sign in and connect a platform to attach images'
                : 'Connect a platform in Settings to attach images'
            }
            attachments={attachments}
            isUploadingAttachments={isUploadingAttachments}
          />
        </AttachmentErrorBoundary>
      )}

      <PostPreviewModal
        isOpen={isPreviewModalOpen}
        originalText={plainText}
        editedText={activeVariant.editedText}
        reasoning={activeVariant.reasoning}
        isLoading={previewLoading}
        error={previewError || activeVariant.error}
        selectedOption={previewSelection}
        genres={previewGenres}
        selectedGenre={selectedGenre}
        onSelectGenre={setSelectedGenre}
        attachments={attachments}
        userAvatarUrl={session?.user?.user_metadata?.avatar_url}
        showClarityEditing
        onSelectOption={setPreviewSelection}
        onEditedTextChange={(value) => {
          setPreviewVariants((prev) => ({
            ...prev,
            [selectedGenre]: {
              ...(prev[selectedGenre] || fallbackVariant),
              editedText: value
            }
          }));
        }}
        onClose={() => setIsPreviewModalOpen(false)}
        onConfirm={handleConfirmPreview}
        onBackToEditing={(text) => {
          setIsPreviewModalOpen(false);
          // If text differs from original, create a new draft with the edited text
          if (text !== plainText) {
            const newDraftId = createDraftWithText(text);
            navigate(`/n/${newDraftId}`);
          }
        }}
      />
      
      <CrosspostModal
        isOpen={isCrosspostModalOpen}
        content={postContent || plainText}
        userId={session?.user?.id || ''}
        attachments={attachments.filter(a => a.url).map(a => ({ type: 'image' as const, url: a.url!, alt: a.alt }))}
        onClose={handleCrosspostModalClose}
        replyToPostId={replyToPostId}
        quotePostId={quotePostId}
        previewMode={crosspostPreviewMode}
      />
    </div>
  );
}

function App() {
  useEffect(() => {
    syncViewportHeightVar();

    window.addEventListener('resize', syncViewportHeightVar);
    window.addEventListener('orientationchange', syncViewportHeightVar);
    window.visualViewport?.addEventListener('resize', syncViewportHeightVar);

    return () => {
      window.removeEventListener('resize', syncViewportHeightVar);
      window.removeEventListener('orientationchange', syncViewportHeightVar);
      window.visualViewport?.removeEventListener('resize', syncViewportHeightVar);
    };
  }, []);

  return (
    <SettingsProvider>
      <PlatformProvider>
        <DraftProvider>
          <Routes>
            <Route path="/" element={<DraftsManager EditorComponent={RouteEditor} />} />
            <Route path="/n/:id" element={
              <DraftRouteGuard>
                <DraftsManager EditorComponent={RouteEditor} />
              </DraftRouteGuard>
            } />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/editor" element={<EditorDemo />} />
            <Route path="*" element={<div style={{ padding: '2rem' }}>Not Found</div>} />
          </Routes>
        </DraftProvider>
      </PlatformProvider>
    </SettingsProvider>
  );
}

export default App; 
